import { postB2BRegistration } from 'api/b2b';
import type { B2BEventType, B2BPublicEventCategory } from 'api/requestTypes';
import { B2bEventDetailsSection } from 'components/b2b/B2bEventDetailsSection/B2bEventDetailsSection';
import { B2bOrganizerDetailsSection } from 'components/b2b/B2bOrganizerDetailsSection/B2bOrganizerDetailsSection';
import {
  B2bTermsAcceptancePanel,
  B2bTermsAndActionsSection,
} from 'components/b2b/B2bTermsAndActionsSection/B2bTermsAndActionsSection';
import type { B2bEventDetailsFieldKey } from 'components/b2b/B2bEventDetailsSection/B2bEventDetailsSection';
import type { B2bOrganizerDetailsFieldKey } from 'components/b2b/B2bOrganizerDetailsSection/B2bOrganizerDetailsSection';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { usePopup } from 'context/PopupContext/PopupContext';
import { useLanguageContext } from 'context/LanguageContext/LanguageContext';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { ReactNode, useEffect } from 'react';
import { useForm, type DefaultValues } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router/routes';
import { colors } from 'styles/colors';
import { pxToRem } from 'styles/pxToRem';
import {
  B2B_REGISTRATION_INTL,
  b2bRegistrationErrorDescriptorForHttpStatus,
} from 'translations/b2bRegistrationIntl';
import {
  B2B_REGISTRATION_API_FIELD_ERROR_MESSAGE_ID,
  extractB2bRegistrationSubmitFieldKeysFromErrorBody,
} from 'utils/b2bRegistrationApiErrors';
import { b2bRegistrationSchema, type B2bRegistrationFormValues } from 'utils/b2bRegistrationSchema';
import { mapB2bFormToCreateRequest } from 'utils/mapB2bFormToCreateRequest';
import { B2B_REGISTRATION_DEMO_CITY_OPTIONS } from './b2bRegistrationDemoCities';
import { B2B_REGISTRATION_COMPLETE_STATE_KEY } from './b2bRegistrationNavigationState';

const B2B_REGISTRATION_DEFAULT_VALUES: DefaultValues<B2bRegistrationFormValues> = {
  fullName: '',
  companyOrOrganization: '',
  phoneNumber: '',
  email: '',
  websiteOrSocialMedia: '',
  hasOrganizedBefore: false,
  previousEvents: '',
  eventName: '',
  description: '',
  eventVenue: '',
  isVenueConfirmed: false,
  termsAccepted: false,
};

export const B2bRegistrationPage = () => {
  const intl = useIntl();
  const { language } = useLanguageContext();
  const formDir = language === 'en' ? 'ltr' : 'rtl';
  const navigate = useNavigate();
  const { showPopup } = usePopup();
  const {
    watch,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<B2bRegistrationFormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(b2bRegistrationSchema),
    defaultValues: B2B_REGISTRATION_DEFAULT_VALUES,
  });

  const hasOrganizedBefore = watch('hasOrganizedBefore');

  useEffect(() => {
    if (!hasOrganizedBefore) {
      setValue('previousEvents', '', { shouldValidate: false });
    }
  }, [hasOrganizedBefore, setValue]);

  const toFieldError = (messageId: string | undefined): ReactNode =>
    messageId != null ? <FormattedMessage id={messageId} defaultMessage={messageId} /> : undefined;

  const organizerErrors: Partial<Record<B2bOrganizerDetailsFieldKey, ReactNode>> = {
    fullName: toFieldError(errors.fullName?.message),
    companyOrOrganization: toFieldError(errors.companyOrOrganization?.message),
    phoneNumber: toFieldError(errors.phoneNumber?.message),
    email: toFieldError(errors.email?.message),
    city: toFieldError(errors.city?.message),
    websiteOrSocialMedia: toFieldError(errors.websiteOrSocialMedia?.message),
    hasOrganizedBefore: toFieldError(errors.hasOrganizedBefore?.message),
    previousEvents: toFieldError(errors.previousEvents?.message),
  };

  const eventErrors: Partial<Record<B2bEventDetailsFieldKey, ReactNode>> = {
    eventName: toFieldError(errors.eventName?.message),
    eventType: toFieldError(errors.eventType?.message),
    publicEventCategory: toFieldError(errors.publicEventCategory?.message),
    description: toFieldError(errors.description?.message),
    eventDate: toFieldError(errors.eventDate?.message),
    eventTime: toFieldError(errors.eventTime?.message),
    eventVenue: toFieldError(errors.eventVenue?.message),
    isVenueConfirmed: toFieldError(errors.isVenueConfirmed?.message),
  };

  const submitRegistration = async (values: B2bRegistrationFormValues) => {
    clearErrors();
    try {
      await postB2BRegistration(mapB2bFormToCreateRequest(values));
      navigate(ROUTES.b2bRegistrationSuccess, {
        replace: true,
        state: { [B2B_REGISTRATION_COMPLETE_STATE_KEY]: true },
      });
    } catch (err) {
      if (!axios.isAxiosError(err) || err.response == null) {
        showPopup({
          message: intl.formatMessage(B2B_REGISTRATION_INTL.error.network),
          type: 'ERROR',
        });
        return;
      }

      const status = err.response.status;

      if (status === 409 || status === 429) {
        const desc = b2bRegistrationErrorDescriptorForHttpStatus(status);
        showPopup({
          message: intl.formatMessage(desc),
          type: 'ERROR',
        });
        return;
      }

      if (status === 400) {
        const fieldKeys = extractB2bRegistrationSubmitFieldKeysFromErrorBody(err.response.data);
        for (const key of fieldKeys) {
          setError(key, {
            type: 'server',
            message: B2B_REGISTRATION_API_FIELD_ERROR_MESSAGE_ID,
          });
        }
        if (fieldKeys.length === 0) {
          showPopup({
            message: intl.formatMessage(B2B_REGISTRATION_INTL.error.generic),
            type: 'ERROR',
          });
        }
        return;
      }

      showPopup({
        message: intl.formatMessage(B2B_REGISTRATION_INTL.error.generic),
        type: 'ERROR',
      });
    }
  };

  const onSubmit = handleSubmit(submitRegistration);

  const registrationCardProps = {
    bg: 'white' as const,
    borderRadius: '2xl',
    borderWidth: '1px',
    borderStyle: 'solid' as const,
    borderColor: colors.b2b.cardBorder,
    boxShadow: '0px 4px 24px rgba(15, 23, 42, 0.06)',
  };

  return (
    <Box
      w='full'
      minW={0}
      px={{ base: 3, sm: 4, lg: 8 }}
      py={{ base: 3, sm: 4, lg: 8 }}
      bg={colors.b2b.footerBg}
      minH='100vh'
      dir={formDir}
      lang={intl.locale}
    >
      <Box maxW={{ base: 'full', lg: '1100px' }} mx='auto' w='full' minW={0}>
        <VStack align='stretch' spacing={{ base: 5, lg: 6 }} w='full'>
          <VStack align='start' spacing={3} w='full' dir={formDir} lang={intl.locale}>
            <Flex
              w='fit-content'
              maxW='100%'
              minW='50.64px'
              minH='46px'
              h='auto'
              px='12px'
              py='8px'
              align='center'
              justify='center'
              flexShrink={0}
              dir={formDir}
              lang={intl.locale}
              whiteSpace={{ base: 'normal', lg: 'nowrap' }}
              textAlign='center'
              borderRadius='43px'
              borderWidth='1px'
              borderStyle='solid'
              borderColor='#FF5C4F'
              bg='#FF5C4F26'
              opacity={1}
              boxSizing='border-box'
            >
              <Text
                as='span'
                fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
                fontWeight='600'
                fontStyle='normal'
                fontSize={pxToRem(16)}
                lineHeight={1.2}
                letterSpacing='0'
                color='#FF5C4F'
                textAlign='center'
                sx={{ leadingTrim: 'none' }}
              >
                <FormattedMessage
                  id='b2b.registration.page.tag'
                  defaultMessage='Register a new event'
                />
              </Text>
            </Flex>
          </VStack>

          <Box
            {...registrationCardProps}
            p={{ base: 4, sm: 6, lg: 10 }}
            w='full'
            minW={0}
            overflow='visible'
          >
            <B2bOrganizerDetailsSection
              cityOptions={B2B_REGISTRATION_DEMO_CITY_OPTIONS}
              fullName={watch('fullName')}
              onFullNameChange={(v) =>
                setValue('fullName', v, { shouldValidate: true, shouldDirty: true })
              }
              companyOrOrganization={watch('companyOrOrganization') ?? ''}
              onCompanyOrOrganizationChange={(v) =>
                setValue('companyOrOrganization', v, { shouldValidate: true, shouldDirty: true })
              }
              phoneNormalizedValue={watch('phoneNumber')}
              onPhoneNormalizedValueChange={(v) =>
                setValue('phoneNumber', v ?? '', { shouldValidate: true, shouldDirty: true })
              }
              email={watch('email') ?? ''}
              onEmailChange={(v) =>
                setValue('email', v, { shouldValidate: true, shouldDirty: true })
              }
              cityValue={watch('city')}
              onCityValueChange={(v) =>
                setValue('city', v ?? '', { shouldValidate: true, shouldDirty: true })
              }
              websiteOrSocialMedia={watch('websiteOrSocialMedia') ?? ''}
              onWebsiteOrSocialMediaChange={(v) =>
                setValue('websiteOrSocialMedia', v, { shouldValidate: true, shouldDirty: true })
              }
              hasOrganizedBefore={watch('hasOrganizedBefore')}
              onHasOrganizedBeforeChange={(v) =>
                setValue('hasOrganizedBefore', v, { shouldValidate: true, shouldDirty: true })
              }
              previousEvents={watch('previousEvents') ?? ''}
              onPreviousEventsChange={(v) =>
                setValue('previousEvents', v, { shouldValidate: true, shouldDirty: true })
              }
              errors={organizerErrors}
              isDisabled={isSubmitting}
              dir={formDir}
              lang={intl.locale}
            />
          </Box>

          <Box
            {...registrationCardProps}
            p={{ base: 4, sm: 6, lg: 10 }}
            w='full'
            minW={0}
            overflow='visible'
          >
            <VStack spacing={4} align='stretch' w='full'>
              <B2bEventDetailsSection
                eventName={watch('eventName')}
                onEventNameChange={(v) =>
                  setValue('eventName', v, { shouldValidate: true, shouldDirty: true })
                }
                eventType={watch('eventType') as B2BEventType | undefined}
                onEventTypeChange={(v) =>
                  setValue('eventType', v, { shouldValidate: true, shouldDirty: true })
                }
                publicEventCategory={
                  watch('publicEventCategory') as B2BPublicEventCategory | undefined
                }
                onPublicEventCategoryChange={(v) =>
                  setValue('publicEventCategory', v, { shouldValidate: true, shouldDirty: true })
                }
                description={watch('description')}
                onDescriptionChange={(v) =>
                  setValue('description', v, { shouldValidate: true, shouldDirty: true })
                }
                eventDate={watch('eventDate')}
                onEventDateChange={(v) =>
                  setValue('eventDate', v, { shouldValidate: true, shouldDirty: true })
                }
                eventTime={watch('eventTime')}
                onEventTimeChange={(v) =>
                  setValue('eventTime', v, { shouldValidate: true, shouldDirty: true })
                }
                eventVenue={watch('eventVenue') ?? ''}
                onEventVenueChange={(v) =>
                  setValue('eventVenue', v, { shouldValidate: true, shouldDirty: true })
                }
                isVenueConfirmed={watch('isVenueConfirmed')}
                onIsVenueConfirmedChange={(v) =>
                  setValue('isVenueConfirmed', v, { shouldValidate: true, shouldDirty: true })
                }
                errors={eventErrors}
                isDisabled={isSubmitting}
                dir={formDir}
                lang={intl.locale}
              />
              <B2bTermsAcceptancePanel
                checked={watch('termsAccepted') === true}
                onChange={(checked) => {
                  setValue('termsAccepted', checked, { shouldValidate: true, shouldDirty: true });
                }}
                isDisabled={isSubmitting}
                isRtlLayout={formDir === 'rtl'}
              />
              {errors.termsAccepted?.message != null ? (
                <Text
                  textStyle='bodySmallRegular'
                  color={colors.red}
                  role='alert'
                  textAlign={formDir === 'rtl' ? 'right' : 'left'}
                  dir={formDir}
                >
                  <FormattedMessage
                    id={errors.termsAccepted.message}
                    defaultMessage={errors.termsAccepted.message}
                  />
                </Text>
              ) : null}
            </VStack>
          </Box>

          <VStack align='stretch' w='full' spacing={3} pt={{ base: 2, lg: 4 }}>
            <B2bTermsAndActionsSection
              omitTermsPanel
              termsAccepted={watch('termsAccepted') === true}
              onTermsAcceptedChange={(checked) => {
                setValue('termsAccepted', checked, { shouldValidate: true, shouldDirty: true });
              }}
              onPrimaryClick={onSubmit}
              primaryLabel={
                <FormattedMessage
                  id='b2b.registration.action.submit'
                  defaultMessage='Submit request'
                />
              }
              onBack={() => navigate(ROUTES.home)}
              isDisabled={isSubmitting}
              isPrimaryLoading={isSubmitting}
              dir={formDir}
              lang={intl.locale}
            />
          </VStack>
        </VStack>
      </Box>
    </Box>
  );
};
